import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import React from "react"
import { useTranslation } from "react-i18next"

import { surfaceColor, surfaceText } from "../../constants/surface-tokens"
import { ContentIcon, toneForLevel } from "./ContentIcon"
import { contentDestination } from "./contentDestination"
import {
  CONTENT_DATETIME_FORMAT,
  formatFact,
  toneBackground,
  toneColor
} from "./formatFact"
import type {
  ContentFact,
  NotificationContent
} from "./notification-content.types"

dayjs.extend(relativeTime)

/**
 * One notification, everywhere it appears.
 *
 * The bell, the notification centre and the Home widget render this same
 * component at three densities, so the project, the customer and the figures
 * cannot be present on one surface and missing on another. What changes
 * between them is spacing and which secondary controls are offered — never
 * the facts.
 */

export type ContentDensity = "compact" | "comfortable"

export interface NotificationContentCardProps {
  content: NotificationContent
  /** When the event happened — always shown apart from any due date. */
  timestamp: string
  unread?: boolean
  level?: unknown
  density?: ContentDensity
  /** Sentence the backend wrote, shown only when there is no content block. */
  fallbackText?: string
  groupCount?: number
  onOpen?: (href: string) => void
  onMarkAsRead?: () => void
  onArchive?: () => void
  /** Secondary controls are offered in the centre, not in the dropdown. */
  showSecondaryActions?: boolean
  testId?: string
}

const ArrowGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width={13}
    height={13}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 12h14M12 6l6 6-6 6" />
  </svg>
)

const Dot = () => (
  <span aria-hidden style={{ color: surfaceColor.chipFg, padding: "0 6px" }}>
    ·
  </span>
)

export function NotificationContentCard({
  content,
  timestamp,
  unread = false,
  level,
  density = "comfortable",
  fallbackText,
  groupCount,
  onOpen,
  onMarkAsRead,
  onArchive,
  showSecondaryActions = false,
  testId
}: NotificationContentCardProps) {
  const { t } = useTranslation()
  const compact = density === "compact"

  const when = dayjs(timestamp).tz()
  const relative = when.isValid() ? when.fromNow() : timestamp
  const absolute = when.isValid()
    ? when.format(CONTENT_DATETIME_FORMAT)
    : undefined

  const href = contentDestination(content?.action?.target)
  const actionKey = content?.action?.key
  const tone = toneForLevel(
    level,
    Boolean(content?.facts?.some((f) => f.tone === "warning"))
  )

  const lead = content?.facts?.find((fact) => fact.emphasis)
  const supporting = (content?.facts ?? []).filter((fact) => !fact.emphasis)

  const title =
    t(`notifications.titles.${content?.category}.${actionKey ?? "default"}`, {
      defaultValue: ""
    }) || content?.title

  const openAction = () => {
    if (href && onOpen) onOpen(href)
  }

  // The button is the affordance; the row stays clickable because that is
  // how the feed has always been used, and a reader should not have to hit a
  // 32px target to open what the row is plainly about.
  const rowOpens = Boolean(href && onOpen)

  return (
    <article
      data-testid={testId}
      data-category={content?.category}
      data-unread={unread || undefined}
      role={rowOpens ? "button" : undefined}
      tabIndex={rowOpens ? 0 : undefined}
      aria-label={rowOpens ? title : undefined}
      onClick={rowOpens ? openAction : undefined}
      onKeyDown={
        rowOpens
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                openAction()
              }
            }
          : undefined
      }
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        padding: compact ? "12px 14px" : "16px",
        minWidth: 0,
        textAlign: "left",
        cursor: rowOpens ? "pointer" : undefined
      }}
    >
      {/* The unread marker is a small teal dot in the gutter, not a tinted
          row: a coloured surface behind every unread item made the severity
          tints unreadable. */}
      <span
        aria-hidden={!unread}
        aria-label={
          unread ? t("notifications.bell.unread", "Unread") : undefined
        }
        style={{
          width: 7,
          height: 7,
          marginTop: 14,
          flex: "none",
          borderRadius: "50%",
          background: unread ? surfaceColor.teal : "transparent"
        }}
      />

      <ContentIcon
        category={content?.category}
        tone={tone}
        size={compact ? 32 : 36}
      />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
          <h3
            style={{
              ...surfaceText.itemTitle,
              fontSize: compact ? 15 : 16,
              color: surfaceColor.ink,
              margin: 0,
              flex: 1,
              minWidth: 0,
              textAlign: "left",
              // A project called "Johnson Residence — Phase 2 Kitchen" has to
              // read in full; an ellipsis here hides the thing being named.
              overflowWrap: "anywhere"
            }}
          >
            {title}
          </h3>
          <time
            dateTime={timestamp}
            title={absolute}
            data-testid="content-timestamp"
            style={{
              ...surfaceText.label,
              color: surfaceColor.inkMuted,
              flex: "none",
              whiteSpace: "nowrap"
            }}
          >
            {relative}
          </time>
        </div>

        <RecordLine content={content} compact={compact} />
        <SubjectLine
          content={content}
          supporting={supporting}
          compact={compact}
        />

        {lead && <LeadFact fact={lead} />}

        {/* The sentence the backend wrote, kept only where the structured
            content says nothing more than the record's name. An enriched row
            states its own facts; repeating "Review project milestones
            planned receivables" underneath them is the generic instruction
            this replaced. */}
        {!content?.subject &&
          (content?.facts?.length ?? 0) === 0 &&
          fallbackText && (
            <p
              style={{
                ...surfaceText.meta,
                color: surfaceColor.inkMuted,
                margin: 0,
                overflowWrap: "anywhere"
              }}
            >
              {fallbackText}
            </p>
          )}

        {content?.resolved === false && (
          <p
            data-testid="content-unresolved"
            style={{
              ...surfaceText.label,
              color: surfaceColor.inkMuted,
              margin: 0
            }}
          >
            {t(
              "notifications.content.recordUnavailable",
              "The record this is about is no longer available to you."
            )}
          </p>
        )}

        {/* The action sits directly under the detail it acts on, and never
            floats to the far edge of a wide card. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 8
          }}
        >
          {href && actionKey && (
            <button
              type="button"
              onClick={openAction}
              data-testid="content-action"
              data-action={actionKey}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                height: 32,
                padding: "0 14px",
                ...surfaceText.button,
                color: surfaceColor.teal,
                background: surfaceColor.surface,
                border: `1px solid ${surfaceColor.accent}`,
                borderRadius: 6,
                cursor: "pointer"
              }}
            >
              {t(
                `notifications.actions.${actionKey}`,
                defaultActionLabel(actionKey)
              )}
              <ArrowGlyph />
            </button>
          )}

          {typeof groupCount === "number" && groupCount > 1 && (
            <span
              data-testid="content-group-count"
              style={{
                ...surfaceText.chip,
                color: surfaceColor.chipFg,
                background: surfaceColor.chipBg,
                borderRadius: 999,
                padding: "3px 9px"
              }}
            >
              {t("notifications.content.updates", "{{count}} updates", {
                count: groupCount
              })}
            </span>
          )}

          {showSecondaryActions && (
            <span
              style={{ display: "inline-flex", gap: 14, marginLeft: "auto" }}
            >
              {unread && onMarkAsRead && (
                <QuietAction
                  onClick={onMarkAsRead}
                  label={t("notifications.bell.markRead")}
                  testId="content-mark-read"
                />
              )}
              {onArchive && (
                <QuietAction
                  onClick={onArchive}
                  label={t("notifications.bell.archive")}
                  testId="content-archive"
                />
              )}
            </span>
          )}
        </div>
      </div>
    </article>
  )
}

function RecordLine({
  content,
  compact
}: {
  content: NotificationContent
  compact: boolean
}) {
  const { t } = useTranslation()
  const record = content?.record
  const customer = content?.customer ?? record?.customer
  if (!record && !customer && !content?.branch) return null

  return (
    <p
      data-testid="content-record"
      style={{
        ...surfaceText.meta,
        fontSize: compact ? 13 : 13,
        color: surfaceColor.inkMuted,
        margin: 0,
        overflowWrap: "anywhere"
      }}
    >
      {record && (
        <>
          <span style={{ color: surfaceColor.ink, fontWeight: 600 }}>
            {record.name}
          </span>
          <span
            data-testid="content-record-kind"
            style={{
              ...surfaceText.chip,
              marginLeft: 6,
              padding: "1px 6px",
              borderRadius: 4,
              background: surfaceColor.chipBg,
              color: surfaceColor.chipFg,
              textTransform: "uppercase",
              letterSpacing: "0.04em"
            }}
          >
            {t(`notifications.content.record.${record.kind}`, record.kind)}
          </span>
        </>
      )}
      {customer && (
        <>
          {record && <Dot />}
          <span data-testid="content-customer">{customer}</span>
        </>
      )}
      {/* Only a viewer who actually navigates branches is shown one. */}
      {content?.branch && (
        <>
          <Dot />
          <span data-testid="content-branch">{content.branch.name}</span>
        </>
      )}
    </p>
  )
}

function SubjectLine({
  content,
  supporting,
  compact
}: {
  content: NotificationContent
  supporting: ContentFact[]
  compact: boolean
}) {
  const { t } = useTranslation()
  const subject = content?.subject
  const actor = content?.actor
  if (!subject?.label && supporting.length === 0 && !actor) return null

  return (
    <p
      data-testid="content-detail"
      style={{
        ...surfaceText.meta,
        fontSize: compact ? 13 : 13,
        color: surfaceColor.inkMuted,
        margin: 0,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        overflowWrap: "anywhere"
      }}
    >
      {subject?.label && (
        <span data-testid="content-subject" style={{ color: surfaceColor.ink }}>
          {subject.label}
        </span>
      )}
      {supporting.map((fact, index) => {
        const { label, value } = formatFact(fact, t)
        return (
          <React.Fragment key={fact.key}>
            {(index > 0 || subject?.label) && <Dot />}
            <span data-testid={`content-fact-${fact.key}`}>
              {fact.kind === "status" ? (
                <span
                  style={{
                    ...surfaceText.chip,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: toneBackground[fact.tone ?? "neutral"],
                    color: toneColor[fact.tone ?? "neutral"]
                  }}
                >
                  {value}
                </span>
              ) : (
                `${label} ${value}`
              )}
            </span>
          </React.Fragment>
        )
      })}
      {actor?.name && (
        <>
          {(subject?.label || supporting.length > 0) && <Dot />}
          <span data-testid="content-actor">
            {t(`notifications.content.actor.${actor.relation}`, {
              name: actor.name,
              defaultValue: "{{name}}"
            })}
          </span>
        </>
      )}
    </p>
  )
}

/** The one figure that explains the row, stated as its own chip. */
function LeadFact({ fact }: { fact: ContentFact }) {
  const { t } = useTranslation()
  const { label, value } = formatFact(fact, t)
  const tone = fact.tone ?? "warning"
  return (
    <span
      data-testid={`content-lead-fact-${fact.key}`}
      style={{
        alignSelf: "flex-start",
        marginTop: 2,
        padding: "4px 10px",
        borderRadius: 6,
        ...surfaceText.label,
        fontWeight: 600,
        background: toneBackground[tone],
        color: toneColor[tone]
      }}
    >
      {fact.direction
        ? value
        : fact.kind === "duration_days"
          ? `${value} ${label.toLowerCase()}`
          : `${label} ${value}`}
    </span>
  )
}

function QuietAction({
  onClick,
  label,
  testId
}: {
  onClick: () => void
  label: string
  testId: string
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        // The row itself opens the record; marking read or archiving must
        // not also navigate away from the list being tidied.
        event.stopPropagation()
        onClick()
      }}
      data-testid={testId}
      style={{
        border: "none",
        background: "none",
        padding: 0,
        cursor: "pointer",
        ...surfaceText.label,
        color: surfaceColor.inkMuted
      }}
    >
      {label}
    </button>
  )
}

function defaultActionLabel(key: string) {
  const words = key.replace(/_/g, " ")
  return words.charAt(0).toUpperCase() + words.slice(1)
}
